import { FeedbackService } from 'src/app/core/services/feedback.service';
import { Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { interval, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ConsentFormsService } from './../../../../core/services/consent-forms.service';
import { getResendCooldown, formatCooldownRemaining } from './../../../../core/services/patient-invite.service';
import { GroupService } from './../../../../core/services/group.service';
import { PatientService } from './../../../../core/services/patient.service';
import { UserService } from './../../../../core/services/user.service';
import { SignaturePadComponent } from './../../../../shared/components/signature-pad/signature-pad.component';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';

@Component({
  selector: 'app-patient-consent-dialog',
  templateUrl: './patient-consent-dialog.component.html',
  styleUrls: ['./patient-consent-dialog.component.scss']
})
export class PatientConsentDialogComponent implements OnInit, OnDestroy {

  form!: FormGroup;
  signNow: boolean = false;
  //consentForms: any[] = [];
  consentForms!: FormArray;
  onboardingConsent: any[] = [];
  procedureConsent: any[] = [];
  consentFormList: any[] = [];
  formsSubscription!: Subscription;
  types: string[] = [];
  groupUsers: any[] = [];
  lastSent: any;
  @ViewChild(SignaturePadComponent) signaturePadComponent!: SignaturePadComponent;
  pendingConsents: any = [];
  nextAllowedResendAt: string | null = null;
  private destroy$ = new Subject<void>();
  cooldownTick = 0;

  /** A state unit (tasks/state-mode.md): the consent is signed here, never emailed. */
  get stateMode(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private groupService: GroupService,
    private patientService: PatientService,
    private consentFormsService: ConsentFormsService,
    private formBuilder: FormBuilder,
    private feedbackService: FeedbackService,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<PatientConsentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any) {

    this.pendingConsents = data.pendingConsents;
    console.log('pendingConsents', this.pendingConsents);
    this.types = data.type || ['all'];
    this.lastSent = data.lastSent || undefined;
    this.nextAllowedResendAt = data.nextAllowedResendAt ?? null;
    this.initializeForm();
  }

  ngOnInit(): void {
    this.subscribeToConsentForms();
    interval(1000).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cooldownTick = Date.now();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.formsSubscription) {
      this.formsSubscription.unsubscribe();
    }
  }

  getResendCooldownState() {
    return getResendCooldown(this.nextAllowedResendAt);
  }

  getResendCooldownMessage(): string {
    const c = getResendCooldown(this.nextAllowedResendAt);
    if (c.canResend) return '';
    return `You can send again in ${formatCooldownRemaining(c.secondsRemaining)}`;
  }

  private initializeForm(): void {
    const patient = this.patientService.patient;
    this.form = this.fb.group({
      patient: [patient],
      email: [patient.email || '', [Validators.required, Validators.email]],
      signature: ['', Validators.required],
      consentForms: [[], Validators.required],
      guardianName: [''],
      signatoryDelegation: [''],
      physicianId: ['', Validators.required],
      signedConsents: [],
      // §14.3 - the shared picker's selection, carried out on `dialogRef.close` with everything
      // else the caller needs. This dialog does not send: it is opened by the patient file, which
      // owns the consent request, so the ids ride out on the result rather than being posted here.
      practiceDocumentIds: [[]]
    });


    this.consentForms = this.formBuilder.array([]);

    this.groupService.groupUsers$
      .subscribe((users: any[]) => {
        if (users?.length) {
          this.groupUsers = users?.filter(u => u.userType === 'doctor' && u.userId !== '511f39e1-ff14-4a6d-b35a-6fcc22923b50');
          const currentUser = this.userService.user;
          const groupOwner = this.groupService.getGroupOwner();
          if (this.groupUsers.findIndex(gu => gu.userId === currentUser.id) > -1) {
            this.form.patchValue({ physicianId: currentUser.id });
          } else if (groupOwner) {
            this.form.patchValue({ physicianId: groupOwner.userId });
          } else {
            this.form.patchValue({ physicianId: this.groupUsers[0].userId })
          }
        }
      });
  }

  private subscribeToConsentForms(): void {
    this.formsSubscription = this.consentFormsService.consentForms$
      .subscribe(forms => {
        this.filterConsentForms(forms);
      });
  }

  private filterConsentForms(forms: any[]): void {
    console.log('filterConsentForms', forms);
    
    // 1) Enrich pending consents with ConsentForm if not already present
    // Use the ConsentForm from pendingConsents if available, otherwise try to match from forms service
    const enrichedPendingConsents = this.pendingConsents.map((pendingConsent: any) => {
      // If ConsentForm is already populated, use it
      if (pendingConsent.ConsentForm) {
        return pendingConsent;
      }
      
      // Otherwise, try to find it in the forms service
      if (forms?.length && pendingConsent.consentFormId) {
        const matchedForm = forms.find(form => form.id === pendingConsent.consentFormId);
        if (matchedForm) {
          return {
            ...pendingConsent,
            ConsentForm: matchedForm
          };
        }
      }
      
      return pendingConsent;
    }).filter((consent: any) => consent.ConsentForm); // Only keep consents where we have a ConsentForm

    if (!enrichedPendingConsents.length) {
      console.warn('No valid consent forms found for pending consents');
      return;
    }

    // 2) Extract ConsentForm objects for grouping
    const consentFormObjects = enrichedPendingConsents.map((consent: any) => consent.ConsentForm);

    // 3) Group into Onboarding vs Procedure
    const onboardingConsent = consentFormObjects.filter((form: any) => form.sendWithInvite === true);
    console.log('onboardingConsent', onboardingConsent);
    const procedureConsent = consentFormObjects.filter((form: any) => form.sendWithInvite === false);
    console.log('procedureConsent', procedureConsent);

    // 4) Store the grouped consents in a single object property
    this.onboardingConsent = onboardingConsent;
    this.procedureConsent = procedureConsent;
    this.consentFormList = [...onboardingConsent, ...procedureConsent];
    
    // 5) Create consent forms using the enriched pending consents
    this.createConsentForms(enrichedPendingConsents);

    // (If your form control expects an object, you can patch that here,
    // or simply patch with the original pendingForms if needed.)
    this.form.patchValue({ consentForms: enrichedPendingConsents });

  }

  private createConsentForms(consentDetails: any[]) {
    this.consentForms = this.formBuilder.array(
      consentDetails.map(detail => {

        const conditionalConsentArray = this.formBuilder.array(
          (detail.ConsentForm?.conditionalConsent || []).map((consent: any) =>
            this.formBuilder.group({
              consentGiven: [true],
              name: [consent]
            })
          )
        );


        const requiredConsent = detail.ConsentForm?.additionalRequiredConsent || [];
        const confirmSide = detail.ConsentForm?.confirmSide
        const sideOptions = detail.ConsentForm?.sideOptions
        const confirmImplant = detail.ConsentForm?.confirmImplant
        const implantOptions = detail.ConsentForm?.implantOptions
        const confirmTransfusion = detail.ConsentForm?.confirmTransfusion
        const transfusionOptions = detail.ConsentForm?.transfusionOptions

        return this.formBuilder.group({
          id: [detail.id, Validators.required],
          inviteToken: [detail.inviteToken, Validators.required],
          consentLabel: [detail.ConsentForm?.label || '', Validators.required],
          consentText: [this.buildHtmlFromText(detail.consentText || detail.ConsentForm?.text) || '', Validators.required],
          consentGiven: [false, Validators.required],
          form: [detail.ConsentForm || null],
          conditionalConsent: conditionalConsentArray,
          additionalRequiredConsent: [requiredConsent],
          selectedRequiredConsent: [requiredConsent.length ? requiredConsent[0] : ''],
          selectedProcedureSide: [''],
          confirmSide: [confirmSide],
          sideOptions: [sideOptions],
          selectedImplant: [''],
          confirmImplant: [confirmImplant],
          implantOptions: [implantOptions],
          selectedTransfusion: [''],
          confirmTransfusion: [confirmTransfusion],
          transfusionOptions: [transfusionOptions],
        });
      })
    );
  }

  /* ══ §14.3 — the shared document attachment picker ══════════════════════════════════
   *
   * PRE-TICKED FROM `sendWithConsentRequest` (§14.6 decision 2), overridable per send.
   *
   * It sits BESIDE the consent forms' own information sheets rather than replacing them. The
   * `ConsentInformationSheet` / `sendSheet` mechanism is a different store (sheets uploaded against
   * one consent form) reached from a different screen, and §14.6 decision 2 retires it into this
   * picker LATER. Merging them here would have meant one of the two silently stopping working
   * during the changeover.
   * ═══════════════════════════════════════════════════════════════════════════════ */

  /** The picker's `[(selection)]`. Mirrored onto the form so it leaves with the result. */
  selectedDocumentIds: string[] = [];

  onDocumentSelectionChange(selection: string[]): void {
    this.selectedDocumentIds = selection || [];
    this.form.patchValue({ practiceDocumentIds: this.selectedDocumentIds });
  }

  /** The PATIENT'S practice, so a multi-practice user is not offered another practice's blanks. */
  get patientGroupId(): string | null {
    const patient = this.patientService.patient;
    return patient ? patient.groupId : null;
  }

  get patientId(): string | null {
    const patient = this.patientService.patient;
    return patient ? patient.id : null;
  }

  get consentFormGroups(): FormGroup[] {
    return (this.consentForms.controls as FormGroup[]);
  }

  getConditionalConsentArray(form: FormGroup): FormArray {
    return form.get('conditionalConsent') as FormArray;
  }


  private buildHtmlFromText(text: string): string {
    if (!text) return text;
    return text.replace(/\n/g, '<br>');
  }


  getSignatureData(base64Image: any) {
    this.form.patchValue({
      signature: base64Image
    });
  }

  onSignNow(): void {
    this.signNow = true;
  }


  fullScreenConsentForm: any = null;
  @ViewChild('fullScreenDialog') fullScreenDialog?: TemplateRef<any>;
  viewConsentFullscreen(consentForm: FormGroup) {
    this.fullScreenConsentForm = consentForm.value;
    this.dialog.open(this.fullScreenDialog!);
  }

  async downloadConsentPDF(consentForm: FormGroup): Promise<void> {
    const consentData = consentForm.value;
    const patient = this.patientService.patient;
    
    await this.consentFormsService.generateConsentPDF(consentData, patient);
  }

  closeDialog(type: string = ''): void {
    if (type === 'email-consent' && !getResendCooldown(this.nextAllowedResendAt).canResend) {
      this.feedbackService.showToast(this.getResendCooldownMessage());
      return;
    }
    const consentFormsValues = this.consentForms.value;


    if (type === 'new-consent') {
      const signatureValid = this.signaturePadComponent.isValid();
      const requiresProcedureSide = consentFormsValues.some((form: any) => form.confirmSide && !form.selectedProcedureSide);
      const requiresImplant = consentFormsValues.some((form: any) => form.confirmImplant && !form.selectedImplant);
      const requiresTransfusion = consentFormsValues.some((form: any) => form.confirmTransfusion && !form.selectedTransfusion);
      const requiresRequiredConsent = consentFormsValues.some((form: any) => form.additionalRequiredConsent?.length && !form.selectedRequiredConsent);
      if (requiresProcedureSide) {
        this.feedbackService.showToast("Please ensure you select a \"Procedure Side\" when completing your consent to continue");
        return;
      }
      if (requiresImplant) {
        this.feedbackService.showToast("Please ensure you select an \"Implant\" when completing your consent to continue");
        return;
      }
      if (requiresTransfusion) {
        this.feedbackService.showToast("Please ensure you select n \"Blood Transfusion\" option when completing your consent to continue");
        return;
      }
      if (requiresRequiredConsent) {
        this.feedbackService.showToast("Please ensure you select a valid \"Required Consent\" option when completing your consent to continue");
        return;
      }
      let isValid = true;
      if (consentFormsValues.some((consent: any) => !consent.consentGiven)) {
        isValid = false;
      }
      if (!signatureValid) {
        isValid = false;
      }
      if (this.signNow && !isValid) {
        this.feedbackService.showToast("Please provide your signature and acceptance of the provided information.");
        return;
      }
    }


    if (this.signNow && this.signaturePadComponent) {

      this.form.patchValue({
        guardianName: this.signaturePadComponent.guardianName,
        signatoryDelegation: this.signaturePadComponent.signatoryDelegation,
        signature: this.signaturePadComponent.getPadData(),
        signedConsents: consentFormsValues,
      });
    }

    setTimeout(() => {
      this.dialogRef.close({ type, ...this.form.value });
    }, 200);
  }

}
